import { useCallback, useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useFeatureFeedbackPrompt } from '~/components/FeatureFeedbackModal/hooks/useFeatureFeedbackPrompt';
import { useIoContext } from '~/contexts';
import { QueryKeys } from '~/hooks/reactQuery/queryKeys';
import { useBusinessChannelEventBinder } from '~/pusher/useBusinessChannelEventBinder';
import { useCompletionToast } from '../TasksDrawerRoot/hooks/useCompletionToast';
import { applyJobPatch, JOB_UPDATE_EVENT_NAME } from '../TasksDrawerRoot/utils/jobUpdatePayload';
import { useTasksJobs } from './useTasksJobs';
const isBulkSend = (payload)=>payload.kind === 'bulkSendProposals';
const isBulkRun = (payload)=>payload.kind === 'bulkSendProposals' || payload.kind === 'bulkCreateDrafts';
const isFinishedBulkRun = (payload)=>isBulkRun(payload) && (payload.status === 'ready' || payload.status === 'failed' || payload.status === 'cancelled');
function settleFeedbackPrompt(drawerStore, payload, promptFeatureFeedback) {
    const { feedbackPrompts, dropFeedbackPrompt } = drawerStore.getState();
    const request = feedbackPrompts.get(payload.jobId);
    if (request == null) return;
    dropFeedbackPrompt(payload.jobId);
    if (payload.status === 'ready') promptFeatureFeedback(request);
}
function awaitedCompletion(awaited, payload) {
    if (payload.status !== 'ready' && payload.status !== 'failed') return null;
    return {
        jobId: awaited.jobId,
        kind: awaited.kind,
        relationshipId: awaited.relationshipId,
        agreementName: awaited.agreementName,
        nextStatus: payload.status,
        downloadUrl: payload.kind === 'relationshipPdf' && payload.status === 'ready' ? payload.downloadUrl : undefined,
        overrides: awaited.overrides
    };
}
export function useJobsListener(param) {
    let { drawerStore, enabled } = param;
    const { optimisticUpdate, refresh } = useTasksJobs({
        refreshOnHookMount: enabled,
        enabled
    });
    const reportCompletion = useCompletionToast();
    const promptFeatureFeedback = useFeatureFeedbackPrompt();
    const queryClient = useQueryClient();
    const onBulkRunFinished = useCallback(()=>{
        void queryClient.invalidateQueries({
            queryKey: [
                QueryKeys.Drafts
            ]
        });
    }, [
        queryClient
    ]);
    const onBulkSendProgressed = useCallback((batchId)=>{
        void queryClient.invalidateQueries({
            queryKey: [
                QueryKeys.BulkSendResults,
                batchId
            ]
        });
    }, [
        queryClient
    ]);
    useJobsChannelBinder({
        optimisticUpdate,
        refresh,
        reportCompletion,
        onBulkRunFinished,
        onBulkSendProgressed,
        promptFeatureFeedback,
        drawerStore,
        enabled
    });
}
function useJobsChannelBinder(param) {
    let { optimisticUpdate, refresh, reportCompletion, onBulkRunFinished, onBulkSendProgressed, promptFeatureFeedback, drawerStore, enabled } = param;
    const { startListening, unbind } = useBusinessChannelEventBinder(JOB_UPDATE_EVENT_NAME, 'scout');
    const { getNow } = useIoContext();
    const unbindRef = useRef(unbind);
    unbindRef.current = unbind;
    useEffect(()=>{
        if (enabled) {
            startListening((payload)=>{
                let matched = false;
                optimisticUpdate((tasks)=>{
                    matched = tasks.some((t)=>t.jobId === payload.jobId);
                    if (!matched) return tasks;
                    const now = getNow().getTime();
                    return tasks.map((t)=>t.jobId === payload.jobId ? applyJobPatch(t, payload, now) : t);
                });
                if (!matched) void refresh();
                if (isBulkSend(payload)) onBulkSendProgressed(payload.jobId);
                if (isFinishedBulkRun(payload)) {
                    onBulkRunFinished();
                    settleFeedbackPrompt(drawerStore, payload, promptFeatureFeedback);
                }
                const { awaitedJobs, dropAwaitedJob } = drawerStore.getState();
                const awaited = awaitedJobs.get(payload.jobId);
                if (awaited == null) return;
                const transition = awaitedCompletion(awaited, payload);
                if (transition == null) return;
                dropAwaitedJob(payload.jobId);
                reportCompletion(transition);
            });
            return ()=>unbindRef.current();
        }
    }, [
        enabled,
        optimisticUpdate,
        refresh,
        drawerStore,
        reportCompletion,
        onBulkRunFinished,
        onBulkSendProgressed,
        promptFeatureFeedback
    ]);
}
